<script lang="ts">
    // Jet/Svelte
    import { createEventDispatcher, onMount } from 'svelte';
    import { getJet } from '~/jet';

    // Stores
    import {
        NowPlayingView,
        fullScreenPlayerState,
    } from '~/stores/full-screen-player';
    import { getI18n } from '~/stores/i18n';
    import { queueMetadata } from '~/stores/queue-metadata';

    // Utilities
    import { fixArtwork } from '~/jet/models';
    import { getPlayback } from '~/utils/playback';

    // Shared Utilities
    import { buildMediaQueryStore } from '@amp/web-app-components/src/stores/media-query';
    import { buildSrc } from '@amp/web-app-components/src/components/Artwork/utils/srcset';
    import { platform } from '@amp/web-apps-utils';

    // Components
    import NowPlayingInfo from '~/components/structure/Players/components/NowPlayingInfo/NowPlayingInfo.svelte';
    import NowPlayingQueue from '~/components/structure/Players/components/NowPlayingQueue/NowPlayingQueue.svelte';
    import PlaybackControls from '@amp/web-app-components/src/components/PlaybackControls/PlaybackControls.svelte';
    import PlaybackItem from '~/components/structure/Players/components/MobileNowPlaying/PlaybackItem.svelte';
    import ViewToggle from '~/components/structure/Players/components/ViewToggle.svelte';

    // Shared Components
    import SharedMobileNowPlaying from '@amp/web-app-components/src/components/MobileNowPlaying/MobileNowPlaying.svelte';
    import VolumeIcon from '@amp/web-app-components/assets/icons/volume.svg';
    import VolumeWavesIcon from '@amp/web-app-components/assets/icons/volume-waves.svg';

    // Minimum viewport aspect ratio required to use landscape view
    // Viewports narrower than this use default "large" view.
    const MOBILE_NOW_PLAYING_LANDSCAPE_ASPECT_RATIO = '14 / 10';

    // Maximum viewport height, in pixels, required to use landscape view
    // Viewports taller than this use default "large" view.
    const MOBILE_NOW_PLAYING_LANDSCAPE_MAX_HEIGHT = 580;

    const nowPlayingItemLayout = buildMediaQueryStore('large', {
        landscape: `(min-aspect-ratio: ${MOBILE_NOW_PLAYING_LANDSCAPE_ASPECT_RATIO}) and (max-height: ${MOBILE_NOW_PLAYING_LANDSCAPE_MAX_HEIGHT}px)`,
        large: '', // Fall back to 'large' when 'landscape' MQ is unmatched
    });

    export let modalTriggerElement: HTMLElement;
    export let disableSlide: boolean;
    export let ariaLabel: string;
    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;

    const jet = getJet();
    const i18n = getI18n();
    const { queue } = getPlayback();
    const isiOS = platform.isiOS();
    const dispatch = createEventDispatcher();

    // Updates contextual menu config to render the <amp-contextual-menu />
    // element somewhere other than the default of document.body
    let parentElement: HTMLElement;

    $: nowPlayingItemId = $queue.nowPlayingItemInfo?.id;
    $: item = !!nowPlayingItemId && $queueMetadata?.get(nowPlayingItemId);
    $: artwork = item?.artwork;

    $: artworkURL = (() => {
        return buildSrc(
            artwork?.template,
            {
                width: 40,
                height: 40,
                crop: 'bb',
                fileType: 'webp',
            },
            {},
        );
    })();
    $: useLandscapeLayout = $nowPlayingItemLayout === 'landscape';
    $: nowPlayingView = $fullScreenPlayerState.nowPlayingView;
    // Queue view is currently not supported in mobile landscape.
    $: isQueueView =
        !useLandscapeLayout && nowPlayingView === NowPlayingView.Queue;
    $: isInfoView =
        !useLandscapeLayout && nowPlayingView === NowPlayingView.Info;
    $: isArtworkView =
        useLandscapeLayout || nowPlayingView === NowPlayingView.Artwork;

    $: isPaused = $queue.derivedStates.isPaused;

    $: playOrPauseLabel = isPaused
        ? $i18n.t('ASE.Web.MusicClassical.Play')
        : $i18n.t('ASE.Web.MusicClassical.Pause');

    const showArtworkView = () => {
        dispatch('toggleView', NowPlayingView.Artwork);
    };

    onMount(() => {
        parentElement = document.querySelector('#mobile-now-playing-dialog');
    });
</script>

<div class="mobile-player-container">
    <SharedMobileNowPlaying
        {modalTriggerElement}
        {disableSlide}
        isPlaybackItemVisible={isArtworkView}
        isQueueVisible={isQueueView}
        isInfoVisible={isInfoView}
        isTextVisible={false}
        {artworkURL}
        hasNowPlayingItem={item !== null}
        {ariaLabel}
        {translateFn}
        on:close
    >
        <svelte:fragment slot="playback-item">
            {#if isArtworkView}
                <PlaybackItem
                    {item}
                    artworkButtonAriaLabel={playOrPauseLabel}
                    layout={useLandscapeLayout ? 'landscape' : 'large'}
                    bind:parentElement
                    on:artworkClick
                />
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="queue">
            {#if isQueueView}
                <NowPlayingQueue
                    isMobileView={true}
                    componentType={'panel'}
                    on:close
                    bind:parentElement
                />
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="info">
            {#if isInfoView}
                <NowPlayingInfo
                    isMobileView={true}
                    componentType={'panel'}
                    bind:parentElement
                />
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="playback-controls">
            <div class="playback-controls-container">
                <div class="playback-controls-progress">
                    <amp-playback-controls-progress data-theme="video" />
                </div>

                <div class="playback-buttons">
                    <PlaybackControls
                        realm="classical"
                        isMobile={true}
                        isNowPlaying={true}
                    />
                </div>

                {#if !isiOS}
                    <div class="volume">
                        <div class="volume-icon">
                            <VolumeIcon aria-hidden="true" />
                        </div>
                        <amp-volume-control />
                        <div class="volume-icon">
                            <VolumeWavesIcon aria-hidden="true" />
                        </div>
                    </div>
                {/if}
            </div>
        </svelte:fragment>

        <ViewToggle slot="now-playing-toggle" on:toggleView />
    </SharedMobileNowPlaying>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .mobile-player-container {
        display: contents;

        :global(.now-playing-structure--queue .now-playing-main),
        :global(.now-playing-structure--info .now-playing-main) {
            --fade-mask-height: 50px;
        }

        .playback-buttons {
            margin-top: auto;
            margin-bottom: auto;
        }

        :global(.music-controls__main) {
            display: flex;
            justify-content: space-evenly;
            gap: 32px;
            flex: 1;
        }

        :global(.playback-controls) {
            --playback-control-icon-width: #{$playback-control-icon-primary-size-large};
            --playback-control-icon-height: #{$playback-control-icon-primary-size-large};
            --playback-control-button-width: #{$playback-control-icon-primary-size-large};
        }

        // the skip buttons are a different size from the play button, so we need to target them more specifically here
        :global(
                .playback-controls-container
                    .playback-controls
                    amp-playback-controls-item-skip
            ) {
            --iconSize: #{$playback-control-icon-secondary-size};
            --playback-control-icon-width: var(--iconSize);
            --playback-control-icon-height: var(--iconSize);
            --playback-control-button-width: #{$playback-control-icon-secondary-size};

            @media (prefers-contrast: more) {
                --skip-control-color: var(--systemPrimary);
            }
        }
    }

    .playback-controls-container {
        // Styles for progress bar and volume slider
        --progress-track-height: #{$playback-progress-track-size-large};
        --progress-track-color: var(--systemTertiary-onDark);
        --progress-track-color-elapsed: var(--systemSecondary-onDark);
        --progress-thumb-color: transparent;
        --progress-thumb-height: #{$playback-progress-thumb-size-large};
        --progress-thumb-offset: -#{$playback-progress-track-size-offset-large}; // Needed for progress bar
        height: 100%;
        display: flex;
        flex-direction: column;

        // Don't show light blue rectangle when interacting with playback controls.
        // TODO: Evaluate whether we should be overriding this default browser behavior
        -webkit-tap-highlight-color: transparent;
    }

    amp-playback-controls-progress {
        flex: 0;
        margin-bottom: 0; // Remove default bottom margin for play button centering
        mix-blend-mode: plus-lighter;
    }

    .volume {
        flex: 0 0 20px; // Fix volume container height
        // Add margin for vertically centering buttons between progress and volume bars
        margin-top: 13px;
        display: flex;
        gap: 8px;
        mix-blend-mode: plus-lighter;
    }

    .volume-icon {
        --volumeIconFill: var(--systemSecondary-onDark);
        flex: 0 0 24px;
        // Center SVG icons inside this container
        display: flex;
        align-items: center;
        justify-content: center;

        :global(svg) {
            height: 14px;
            fill: var(--volumeIconFill);
        }
    }

    amp-volume-control {
        flex: 1;
    }

    @media (orientation: landscape) {
        .playback-controls {
            gap: 8px;
        }

        .music-controls {
            --iconSize: 60px;
        }

        :global(.playback-controls amp-playback-controls-item-skip) {
            --iconSize: 44px;
        }
    }

    @media (prefers-contrast: more) {
        .playback-controls {
            --playback-controls-play-color: var(--systemPrimary);
            --progress-track-color: var(--systemQuinary);
            --progress-track-color-elapsed: var(--systemQuaternary);
            --progress-time: var(--systemQuaternary);
            --skip-control-color: var(--systemPrimary);
        }

        .live-progress {
            --liveProgressGradientColor: var(--systemQuinary);
        }

        .volume-icon {
            --volumeIconFill: var(--systemQuaternary);
        }

        amp-playback-controls-progress,
        .live-progress,
        .volume {
            mix-blend-mode: normal;
        }
    }
</style>
